import { DebugValidationResolverMetaKey } from '@framework/validation/methods/debug/debugValidationResolver.ts';
import type React from 'react';
import { get, type UseFormReturn } from 'react-hook-form';
import { type AnyObject, type ObjectSchema } from 'yup';
import { setByPath, unsetByPath } from '../utils/pathAccessors.ts';
import { yupMemoizedResolver } from './yupMemoizedResolver.ts';

const DEBUG_VALIDATION = false;

export interface IResolverErrorInjectionRefs {
  controlRef: React.MutableRefObject<UseFormReturn['control'] | null>;
}

export function createYupAugmentedResolver(
  schema: ObjectSchema<AnyObject>,
  injectionRefs?: IResolverErrorInjectionRefs,
) {
  const resolver = yupMemoizedResolver(schema);

  return async (data: any, context: any, options: any) => {
    const result = await resolver(data, context, options);

    if (process.env.NODE_ENV === 'test' && process.env.DEBUG_VALIDATION) {
      console.log('[yupResolver] Validation:', result, 'Data:', data);
    }

    if (process.env.NODE_ENV === 'development') {
      if (schema.meta()?.[DebugValidationResolverMetaKey] || DEBUG_VALIDATION) {
        console.log('[yupResolver] Validation:', result, 'Data:', data);
      }
    }

    // Patch RHF: inietta errori per i path modificati da syncComputedValues.
    // RHF normalmente scarta errori cross-field (applica solo l'errore del
    // campo che ha triggerato la validazione). Qui sfruttiamo il fatto che
    // il resolver ha GIÀ validato tutto: iniettiamo gli errori per i
    // pendingErrorPaths direttamente in _formState.errors.
    //
    // NOTE: Ci basiamo sull'assunto che tutti gli errori siano sempre
    // impostabili, e che il fatto che si vedano o meno dipenda solo dall'
    // `isTouched` del campo (vedi nostro FormItem)
    injectPendingErrors(result, injectionRefs);

    return result;
  };
}

/**
 * Inietta errori cross-field del resolver in `_formState.errors`.
 *
 * RHF aggiorna errors solo per il campo che ha triggerato la validazione
 * (blur/trigger), scartando errori cross-field. Qui confrontiamo il
 * risultato completo del resolver con gli errori attuali e aggiorniamo
 * SOLO i campi il cui errore è effettivamente cambiato.
 *
 * Il broadcast usa `name` per ogni campo modificato, così
 * `shouldSubscribeByName` filtra e solo il FormItem interessato
 * si aggiorna: nessun re-render globale.
 */
function injectPendingErrors(
  resolverResult: { errors: Record<string, any>; values: any },
  refs?: IResolverErrorInjectionRefs,
) {
  if (!refs) return;
  const control = refs.controlRef.current;
  if (!control) return;

  // Durante il submit RHF gestisce già tutti gli errori
  // (_formState.errors = errors + broadcast globale). Saltare l'iniezione
  // evita broadcast ridondanti e re-render pesanti.
  if (control._formState.isSubmitting) return;

  const formErrors = control._formState.errors;
  const resolverErrors = resolverResult.errors;
  const stateNext = control._subjects.state.next;

  for (const name of control._names.mount) {
    const newError = get(resolverErrors, name);
    const oldError = get(formErrors, name);

    if (newError) {
      if (
        !oldError ||
        oldError.message !== newError.message ||
        oldError.type !== newError.type
      ) {
        setByPath(formErrors, name, newError);
        stateNext({ name, errors: formErrors });
      }
    } else if (oldError) {
      unsetByPath(formErrors, name);
      stateNext({ name, errors: formErrors });
    }
  }
}
